import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, Loader2, Lock } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, register } = useAuth();
  const sessionId = params.get("session_id");
  const orderIdParam = params.get("order_id");
  const gateway = params.get("gateway");
  const [state, setState] = useState({ status: "checking", order_id: null, message: "" });
  const [order, setOrder] = useState(null);
  const [showAccount, setShowAccount] = useState(false);
  const [signup, setSignup] = useState({ name: "", email: "", password: "" });
  const [signupBusy, setSignupBusy] = useState(false);
  const [signupErr, setSignupErr] = useState("");
  const attemptsRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    // Razorpay flow: order is verified server-side before we land here
    if (gateway === "razorpay" && orderIdParam) {
      (async () => {
        try {
          const o = await api.get(`/orders/${orderIdParam}`);
          if (cancelled) return;
          setOrder(o.data);
          setState({ status: "paid", order_id: orderIdParam, message: "" });
          if (!user && !o.data.user_id) {
            const guestEmail = localStorage.getItem("tmf_guest_email") || o.data.email || "";
            const guestName = o.data.shipping?.name || "";
            setSignup((s) => ({ ...s, email: guestEmail, name: guestName }));
            setShowAccount(true);
          }
        } catch (e) {
          setState({ status: "error", message: "Unable to fetch order" });
        }
      })();
      return () => { cancelled = true; };
    }

    if (!sessionId) { setState({ status: "error", message: "Missing session id" }); return; }

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", order_id: data.order_id, message: "" });
          // Fetch order to inspect if guest
          try {
            const o = await api.get(`/orders/${data.order_id}`);
            setOrder(o.data);
            if (!user && !o.data.user_id) {
              const guestEmail = localStorage.getItem("tmf_guest_email") || o.data.email || "";
              const guestName = o.data.shipping?.name || "";
              setSignup((s) => ({ ...s, email: guestEmail, name: guestName }));
              setShowAccount(true);
            }
          } catch {
            // Order fetch is best-effort — payment is already confirmed, keep the success state.
          }
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ status: "failed", message: "Payment " + data.payment_status });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current > 12) {
          setState({ status: "pending", order_id: data.order_id, message: "Payment is still processing. We'll email you once confirmed." });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        attemptsRef.current += 1;
        if (attemptsRef.current > 12) { setState({ status: "error", message: "Unable to fetch payment status" }); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, orderIdParam, gateway, user]);

  const doSignup = async (e) => {
    e.preventDefault();
    setSignupBusy(true); setSignupErr("");
    try {
      await register(signup.email, signup.password, signup.name);
      localStorage.removeItem("tmf_guest_email");
      localStorage.removeItem("tmf_last_order_id");
      toast.success("Account created — order saved to your history");
      nav("/account", { replace: true });
    } catch (er) {
      setSignupErr(formatApiError(er.response?.data?.detail) || "Signup failed");
    } finally { setSignupBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-16 animate-tmf-fade">
      {state.status === "checking" && (
        <div className="text-center py-10">
          <Loader2 size={64} className="mx-auto text-tmf-blue animate-spin mb-6" />
          <h1 className="font-display font-black text-3xl uppercase tracking-tight text-tmf-navy">Confirming Payment…</h1>
          <p className="mt-2 text-slate-600">Please don't close this window.</p>
        </div>
      )}

      {state.status === "paid" && (
        <>
          <div className="text-center py-8">
            <CheckCircle2 size={72} className="mx-auto text-green-600 mb-6 animate-tmf-rise" />
            <h1 data-testid="payment-success-heading" className="font-display font-black text-4xl uppercase tracking-tight text-tmf-navy">Payment Confirmed</h1>
            <p className="mt-3 text-slate-600">Your order has been placed. We'll ship it directly from our factory.</p>
            {state.order_id && (
              <div className="mt-6 inline-block border border-tmf-navy px-6 py-3">
                <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Order ID</div>
                <div className="font-mono-tmf font-bold text-lg text-tmf-navy">{state.order_id}</div>
              </div>
            )}
          </div>

          {showAccount ? (
            <form onSubmit={doSignup} className="mt-8 border-2 border-tmf-blue bg-white p-8 animate-tmf-rise" style={{ animationDelay: "200ms" }}>
              <div className="flex items-center gap-2 mb-2">
                <Lock size={18} className="text-tmf-blue" />
                <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue font-bold">Required · Save your order</div>
              </div>
              <h2 className="font-display font-black text-2xl uppercase tracking-tight text-tmf-navy mb-2">Create your TMF account</h2>
              <p className="text-sm text-slate-600 mb-6">
                One quick step to link your order, get shipping updates, and download invoices later.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Full Name</label>
                  <Input data-testid="pw-name" required value={signup.name} onChange={(e) => setSignup({ ...signup, name: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Email</label>
                  <Input data-testid="pw-email" required type="email" value={signup.email} onChange={(e) => setSignup({ ...signup, email: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500">Password (min 6 chars)</label>
                  <Input data-testid="pw-password" required type="password" minLength={6} value={signup.password} onChange={(e) => setSignup({ ...signup, password: e.target.value })} className="rounded-none mt-1" />
                </div>
              </div>
              {signupErr && <div data-testid="pw-error" className="mt-4 text-sm text-tmf-orange border border-orange-300 bg-orange-50 px-3 py-2">{signupErr}</div>}
              <Button data-testid="pw-submit" type="submit" disabled={signupBusy} className="rounded-none w-full mt-6 bg-tmf-blue hover:bg-tmf-navy h-12 uppercase tracking-widest text-sm font-bold hover-lift">
                {signupBusy ? "Creating account…" : "Create Account & Save Order"}
              </Button>
            </form>
          ) : (
            <div className="mt-8 flex gap-3 justify-center">
              <Link to="/account" className="border border-tmf-navy text-tmf-navy hover:bg-tmf-navy hover:text-white px-5 py-3 text-xs font-bold uppercase tracking-widest hover-lift">View Orders</Link>
              <Link to="/products" className="bg-tmf-blue hover:bg-tmf-navy text-white px-5 py-3 text-xs font-bold uppercase tracking-widest hover-lift">Keep Shopping</Link>
            </div>
          )}
        </>
      )}

      {(state.status === "failed" || state.status === "error" || state.status === "pending") && (
        <div className="text-center py-10">
          <XCircle size={72} className="mx-auto text-tmf-navy mb-6" />
          <h1 className="font-display font-black text-4xl uppercase tracking-tight text-tmf-navy">{state.status === "pending" ? "Still processing" : "Payment issue"}</h1>
          <p className="mt-3 text-slate-600">{state.message}</p>
          <div className="mt-10 flex gap-3 justify-center">
            <Link to="/cart" className="border border-tmf-navy text-tmf-navy hover:bg-tmf-navy hover:text-white px-5 py-3 text-xs font-bold uppercase tracking-widest">Back to Cart</Link>
            <Link to="/products" className="bg-tmf-blue hover:bg-tmf-navy text-white px-5 py-3 text-xs font-bold uppercase tracking-widest">Keep Shopping</Link>
          </div>
        </div>
      )}
    </div>
  );
}
